import { requestJson } from "./client.js";
import { cologuardApiConfig } from "./config.js";
import { getCampaignSuccessMessage } from "../utils/customerMessages.js";

export async function createCologuardCampaign(payload, { accessToken = "", signal } = {}) {
  const result = await requestJson({
    baseUrl: cologuardApiConfig.campaignBaseUrl,
    path: "/SPS_CG_Campaign",
    method: "POST",
    accessToken,
    body: payload,
    signal,
    fallbackMessage: "We couldn't create this campaign. Please try again.",
  });

  return {
    message: result.message || "Campaign created successfully.",
    campaign: result.stored_campaign || result.campaign || null,
    batchPlan: result.batch_plan || null,
  };
}

export async function generateCologuardCampaign(payload, { accessToken = "", signal } = {}) {
  const result = await requestJson({
    baseUrl: cologuardApiConfig.campaignBaseUrl,
    path: "/SPS_CG_Campaign_Generate",
    method: "POST",
    accessToken,
    body: payload,
    signal,
    fallbackMessage: "The campaign was saved, but code preparation could not start.",
  });

  return {
    message: getCampaignSuccessMessage(result.action),
    action: result.action || "generation_queued",
    stageTarget: result.stage_target || null,
    magentoRule: result.magento_rule || null,
    couponGeneration: result.coupon_generation || null,
    campaign: result.campaign || null,
    note: result.note || "",
  };
}

export function getCologuardCampaigns({
  campaignId = "",
  createdByUserId = "",
  campaignDesignator = "",
  campaignStatus = "",
  createdFrom = "",
  createdTo = "",
  limit = 10,
  cursor = "",
  accessToken = "",
  signal,
} = {}) {
  return requestJson({
    baseUrl: cologuardApiConfig.campaignBaseUrl,
    path: "/SPS_CG_Campaign",
    accessToken,
    signal,
    query: {
      campaign_id: campaignId.trim(),
      created_by_user_id: createdByUserId.trim(),
      campaign_designator: campaignDesignator.trim().toUpperCase(),
      campaign_status: campaignStatus.trim().toLowerCase(),
      created_from: createdFrom.trim(),
      created_to: createdTo.trim(),
      limit,
      cursor: cursor.trim(),
    },
    fallbackMessage: "We couldn't load campaigns. Please try again.",
  });
}

export async function getCologuardCampaignById({ campaignId, accessToken = "", signal } = {}) {
  const result = await getCologuardCampaigns({ campaignId, accessToken, signal, limit: 1 });
  const campaign = result.campaign || result.campaigns?.[0] || null;
  if (!campaign) {
    throw new Error("Campaign not found.");
  }
  return campaign;
}

export function getCologuardHistory({
  campaignDesignator = "",
  limit = 18,
  cursor = "",
  accessToken = "",
  signal,
} = {}) {
  return requestJson({
    baseUrl: cologuardApiConfig.historyBaseUrl,
    path: "/SPS_CG_History",
    accessToken,
    signal,
    query: {
      campaignDesignator: campaignDesignator.trim().toUpperCase(),
      limit,
      cursor: cursor.trim(),
    },
    fallbackMessage: "We couldn't load activity history. Please try again.",
  });
}

export function getCologuardGroupedHistory({
  campaignDesignator = "",
  limit = 10,
  cursor = "",
  accessToken = "",
  signal,
} = {}) {
  return requestJson({
    baseUrl: cologuardApiConfig.historyBaseUrl,
    path: "/SPS_CG_History_Grouped",
    accessToken,
    signal,
    query: {
      campaignDesignator: campaignDesignator.trim().toUpperCase(),
      limit,
      cursor: cursor.trim(),
    },
    fallbackMessage: "We couldn't load campaign history. Please try again.",
  });
}

export function getCologuardDashboardMetrics({
  activityDays = 7,
  campaignDesignators = [],
  accessToken = "",
  signal,
} = {}) {
  return requestJson({
    baseUrl: cologuardApiConfig.metricsBaseUrl,
    path: "/SPS_CG_Metrics",
    accessToken,
    signal,
    query: {
      activity_days: activityDays,
      campaign_designators: campaignDesignators.join(","),
    },
    fallbackMessage: "We couldn't load dashboard activity. Please try again.",
  });
}

export function getCologuardUsers({
  email = "",
  role = "",
  status = "",
  active = "",
  limit = 20,
  accessToken = "",
  signal,
} = {}) {
  return requestJson({
    baseUrl: cologuardApiConfig.userBaseUrl,
    path: "/SPS_CG_Users",
    accessToken,
    signal,
    query: {
      email: email.trim().toLowerCase(),
      role: role.trim().toLowerCase(),
      status: status.trim().toLowerCase(),
      active,
      limit,
    },
    fallbackMessage: "We couldn't load users. Please try again.",
  });
}

export function createCologuardUser(payload, { accessToken = "", signal } = {}) {
  return requestJson({
    baseUrl: cologuardApiConfig.userBaseUrl,
    path: "/SPS_CG_User",
    method: "POST",
    accessToken,
    body: payload,
    signal,
    fallbackMessage: "We couldn't invite this user. Please try again.",
  });
}
